import axios from "axios";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { Settings } from "lucide-react";
import Swal from "sweetalert2";

// Sidebar brand row (reference design)
const SidebarBrand = () => (
  <div className="sb-brand">
    <div className="sb-brand__logo">
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    </div>
    <div>
      <div className="sb-brand__title">MyPackGenie</div>
      <div className="sb-brand__sub">Admin Console</div>
    </div>
  </div>
);

// Sidebar section caption (Workspace / Marketplace / Catalog)
const Caption = ({ children }) => (
  <li className="sb-cap-li">
    <span className="sb-cap">{children}</span>
  </li>
);

// Sidebar menu search — filters the visible top-level menu items by label
const SidebarSearch = () => {
  const handleSearch = (e) => {
    const q = e.target.value.trim().toLowerCase();
    const nav = e.target.closest("nav#sidebar");
    if (!nav) return;
    nav.querySelectorAll("ul.menu-categories > li.menu").forEach((li) => {
      const text = (li.textContent || "").toLowerCase();
      li.style.display = !q || text.includes(q) ? "" : "none";
    });
  };
  return (
    <div className="sb-search">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        type="text"
        placeholder="Search menu…"
        onChange={handleSearch}
        aria-label="Search menu"
      />
    </div>
  );
};

// Sidebar user card pinned to the bottom (reference design)
const SidebarUserCard = ({ user }) => {
  const name = user?.name || user?.user_type || "User";
  const email = user?.email || "";
  const initials =
    (name || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "U";
  return (
    <div className="sb-usercard">
      <div className="sb-usercard__avatar">{initials}</div>
      <div style={{ minWidth: 0, lineHeight: 1.15 }}>
        <div className="sb-usercard__name">{name}</div>
        {email ? <div className="sb-usercard__mail">{email}</div> : null}
      </div>
    </div>
  );
};

const Admin_Sidebar = () => {
  const { pathname } = useLocation();
  const parts = pathname.split("/"); // ['', 'admin', 'buyer', 'product-list', '2']
  const lastPart = parts[parts.length - 1]; // '2'
  const productId = parts[parts.length - 2]; // '45'
  const orderId = parts[parts.length - 1]; // '23'

  const user = localStorage.getItem("user");
  const userData = JSON.parse(user);
  const userType = userData?.user_type;
  const sub_admin_id = userData?.sub_admin_id;
  const [isActive, setIsActive] = useState(userData?.status === "active");
  const [accessModules, setAccessModules] = useState([]);
  const [accessSubModuleModules, setAccessSubModuleModules] = useState([]);
  const isSubAdminWithId1 = userType === "subadmin" && sub_admin_id == 1;

  useEffect(() => {
    if (userType === "subadmin") {
      axios
        .get(
          `${process.env.REACT_APP_URL}/admin/sub_admin_module_permissions`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          },
        )
        .then((res) => {
          const permissions = res.data.data.permissions || [];

          // Filter permissions for the current user
          const userPermissions = permissions.filter(
            (item) => item.user_id === userData?.user_id,
          );

          // Extract modules and subModules
          const modules = Array.from(
            new Set(
              userPermissions.map((item) => item.module?.name).filter(Boolean), // remove null/undefined
            ),
          );

          const subModules = Array.from(
            new Set(
              userPermissions
                .map((item) => item.subModule?.name)
                .filter(Boolean), // remove null/undefined
            ),
          );

          setAccessModules(modules);
          setAccessSubModuleModules(subModules);
        })
        .catch((err) => {
          if (err.response?.data?.message === "Unauthorized!") {
            // window.location.replace("/");
          }
        });
    }
  }, [userType, userData?.user_id]);

  useEffect(() => {
    // Only for supplier users
    if (userType !== "supplier" || !userData?.user_id) return;

    const fetchUserData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_URL}/admin/users/${userData.user_id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          },
        );

        const updatedUser = res.data.data; // same structure as /account fetch
        if (updatedUser) {
          localStorage.setItem("user", JSON.stringify(updatedUser));
          setIsActive(updatedUser?.status === "active");
        }
      } catch (err) {
        console.error("Error fetching updated user data:", err);
      }
    };

    // ✅ run immediately when sidebar mounts (on refresh or direct load)
    fetchUserData();
  }, [userType]);

  let product = userType === "supplier" ? "My Product" : "Product";
  let AssignOrder =
    userType === "supplier" ? "My Assigned Orders" : " Assigned Orders";

  const buyer = ["/admin/buyer/list", `/admin/buyer/product-list/${lastPart}`];
  const RequestForQuotation = ["/admin/request-for-quotation/list"];

  const Supplier = ["/admin/supplier/list", "admin/supplier/show"];
  const Connection = ["/admin/connection/list", "/admin/supplier/show"];
  const SupplierBuyLead = ["/admin/supplier-by-lead/list"];

  const SupplierProduct = ["/admin/supplier/product/list"];
  const BuyerManagement = [
    "/admin/buyer/list",
    "/admin/request-for-quotation/list",
    "/admin/supplier-by-lead/list",
    `/admin/buyer/product-list/${lastPart}`,
    `/admin/revert-back/supplier/${productId}/${orderId}`,
    "/admin/rfq-product-show",
    "/admin/buyer/show",
    "/admin/assign-supplier",
    "/admin/buyer/assign-orders",
    "/admin/buyer/add",
    "/admin/supplier-offline-assign",
    `/admin/supplier-offline-product-list/${lastPart}`,
    "/admin/buyer-fake/add",
    "/admin/supplier-fake/add",
  ];
  const SupplierManagement = [
    "/admin/supplier/list",
    "/admin/supplier/product/list",
    "/admin/connection/list",
    "/admin/supplier/show",
    "/admin/supplier-product-show",
    "/admin/supplier/add",
    "/admin/inbound",
    "/admin/outbound",
    "/admin/supplier-buy-lead-add",
  ];
  const GraphicalDesignManagement = [
    "/admin/graphic-designer",
    "/admin/graphic-designer/add",
    "/admin/graphic-designer/show",
    "/admin/graphic-designer/edit",
    "/admin/graphic-designer/connection/list",
  ];

  const UserManagement = [
    "/admin/UserManagement/list",
    "/admin/usermanagement/add",
    "/admin/usermanagement/show",
    "/admin/usermanagement/edit",
  ];

  const Admin_roles = [
    "/admin/role",
    "/admin/role/add",
    "/admin/role/edit",
    "/admin/role/show",
  ];

  const Admin_permission = [
    "/admin/permision",
    "/admin/permision/add",
    "/admin/permision/edit",
    "/admin/permision/show",
  ];
  const Master = [
    "/admin/permision",
    "/admin/permision/add",
    "/admin/permision/edit",
    "/admin/permision/show",
    "/admin/product",
    "/admin/product/add",
    "/admin/product/edit",
    "/admin/product/show",
    "/admin/cms",
    "/admin/cms/add",
    "/admin/cms/edit",
    "/admin/cms/show",
    "/admin/category",
    "/admin/category/add",
    "/admin/category/edit",
    "/admin/category/show",
    "/admin/sub-category",
    "/admin/sub-category/add",
    "/admin/sub-category/edit",
    "/admin/sub-category/show",
    "/admin/blog-category",
    "/admin/blog-category/add",
    "/admin/blog-category/edit",
    "/admin/blog-category/show",
    "/admin/blog",
    "/admin/blog/add",
    "/admin/blog/edit",
    "/admin/blog/show",
    "/admin/solution",
    "/admin/solution/add",
    "/admin/solution/edit",
    "/admin/solution/show",
    "/admin/news-letters",
    " /admin/contacts",
  ];

  const Admin_Cms = [
    "/admin/cms",
    "/admin/cms/add",
    "/admin/cms/edit",
    "/admin/cms/show",
  ];

  const Contacts = ["/admin/contacts"];
  const Consultations = ["/admin/consultations"];

  const Product = [
    "/admin/product",
    "/admin/product/add",
    "/admin/product/edit",
    "/admin/product/show",
  ];

  const Category = [
    "/admin/category",
    "/admin/category/add",
    "/admin/category/edit",
    "/admin/category/show",
  ];

  const SubCategory = [
    "/admin/sub-category",
    "/admin/sub-category/add",
    "/admin/sub-category/edit",
    "/admin/sub-category/show",
  ];
  const BlogCategory = [
    "/admin/blog-category",
    "/admin/blog-category/add",
    "/admin/blog-category/edit",
    "/admin/blog-category/show",
  ];

  const Blog = [
    "/admin/blog",
    "/admin/blog/add",
    "/admin/blog/edit",
    "/admin/blog/show",
  ];

  const Solution = [
    "/admin/solution",
    "/admin/solution/add",
    "/admin/solution/edit",
    "/admin/solution/show",
  ];

  const AssignOrderCode = [
    "/admin/assign-order",
    "/admin/assign-order/add",
    "/admin/assign-order/edit",
    "/admin/assign-order/show",
  ];

  const DesignStudio = [
    "/admin/design-studio",
    "/admin/design-studio/add",
    "/admin/design-studio/edit",
    "/admin/design-studio/show",
  ];

  const NewsLetter = ["/admin/news-letters"];

  const Admin_roles_permission = ["/admin/role-permission"];

  const LogoutAdmin = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then((result) => {
      if (result.isConfirmed) {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        // Clear current tab's storage
        localStorage.removeItem("Token");
        localStorage.removeItem("user");

        toast.success("Logout Successfully!");

        if (user.user_type === "admin") {
          window.location.replace("/");
        } else if (user.user_type === "supplier") {
          // Was hardcoded to the old thoughtexecuted.com domain, which no
          // longer resolves — the storefront moved to webglowx.com.
          window.location.replace(
            `${process.env.REACT_APP_STOREFRONT_URL || "https://mypackgenie.webglowx.com"}?logout=true`,
          );
        } else {
          window.location.replace("/");
        }
      }
    });
  };

  if (userType === "subadmin") {
    return (
      <>
        <style>{`
  @media screen and (min-width: 768px) {
    .mobile-only {
      display: none;
    }
  }
`}</style>
        <div className="sidebar-wrapper sidebar-theme">
          <nav id="sidebar">
            <SidebarBrand />
            <SidebarSearch />
            <div className="shadow-bottom"></div>
            <div className="d-flex justify-content-end">
              <a
                href="/#"
                className="sidebarCollapse mr-2 d-sm-none"
                data-placement="bottom"
              >
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-x"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </a>
            </div>

            <ul
              className="list-unstyled menu-categories scroll-ui"
              id="accordionExample"
              style={{ overflowY: "scroll" }}
            >
              <li
                className="menu mobile-only"
                style={{ borderBottom: "1px solid #e0e0e0", marginTop: "15px" }}
              >
                <NavLink
                  to="/admin/profile-update"
                  data-active={
                    pathname === "/admin/profile-update" ? "true" : "false"
                  }
                  aria-expanded={
                    pathname === "/admin/profile-update" ? "true" : "false"
                  }
                  className="dropdown-toggle"
                >
                  <div className="flex items-center">
                    <img
                      src="/assets/img/MyPackGenie Final.jpg"
                      alt="Profile Icon"
                      width="24"
                      height="24"
                      className="mr-2"
                      style={{ objectFit: "contain" }}
                    />
                    <span
                      className="font-bold"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      Profile
                    </span>
                  </div>
                </NavLink>
              </li>

              {(isSubAdminWithId1 ||
                accessModules.includes("Dashboard") ||
                accessModules.includes("User Management")) && (
                <Caption>Workspace</Caption>
              )}

              {(isSubAdminWithId1 || accessModules.includes("Dashboard")) && (
                <li
                  className="menu"
                  style={{
                    borderBottom: "1px solid #e0e0e0",
                    marginTop: "15px",
                  }}
                >
                  <NavLink
                    to="/admin/dashboard"
                    data-active={
                      pathname === "/admin/dashboard" ? "true" : "false"
                    }
                    aria-expanded={
                      pathname === "/admin/dashboard" ? "true" : "false"
                    }
                    className="dropdown-toggle"
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-home"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        Dashboard
                      </span>
                    </div>
                  </NavLink>
                </li>
              )}

              {/* {accessModules.includes("New Registration") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <NavLink
                    to="/admin/new-registration"
                    data-active={
                      pathname === "/admin/new-registration" ? "true" : "false"
                    }
                    aria-expanded={
                      pathname === "/admin/new-registration" ? "true" : "false"
                    }
                    className="dropdown-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.8}
                        stroke="currentColor"
                        className="text-blue-600"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M18 9v6M21 12h-6M7.5 7.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM4 19.5a5.5 5.5 0 0 1 11 0v.25a.25.25 0 0 1-.25.25H4.25a.25.25 0 0 1-.25-.25v-.25Z"
                        />
                      </svg>

                      <span className="text-sm font-semibold text-gray-800">
                        New Registration
                      </span>
                    </div>
                  </NavLink>
                </li>
              )} */}

              {accessModules.includes("User Management") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <NavLink
                    to="/admin/UserManagement/list"
                    data-active={
                      UserManagement.includes(pathname) ? "true" : "false"
                    }
                    aria-expanded={
                      UserManagement.includes(pathname) ? "true" : "false"
                    }
                    className="dropdown-toggle"
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 100 100"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="studio-scg-icon"
                      >
                        <circle cx="50" cy="50" r="45" />
                        <text
                          x="50%"
                          y="55%"
                          textAnchor="middle"
                          fontSize="28"
                          fontWeight="bold"
                          fill="currentColor"
                          fontFamily="Arial, sans-serif"
                        >
                          SCG
                        </text>
                      </svg>
                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        User Management
                      </span>
                    </div>
                  </NavLink>
                </li>
              )}

              {(accessModules.includes("Buyer Management") ||
                accessModules.includes("Supplier Management") ||
                accessModules.includes("GD Management") ||
                accessModules.includes("Assigned Orders") ||
                accessModules.includes("Assign Order") ||
                isSubAdminWithId1 ||
                accessModules.includes("Design Studio")) && (
                <Caption>Marketplace</Caption>
              )}

              {accessModules.includes("Buyer Management") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <a
                    href="#buyerMenu"
                    data-toggle="collapse"
                    aria-expanded={
                      BuyerManagement.includes(pathname) ? "true" : "false"
                    }
                    className={
                      BuyerManagement.includes(pathname)
                        ? "dropdown-toggle collapsed bg-white"
                        : "dropdown-toggle"
                    }
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-layers"
                      >
                        <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                        <polyline points="2 17 12 22 22 17"></polyline>
                        <polyline points="2 12 12 17 22 12"></polyline>
                      </svg>
                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        Buyer Management
                      </span>
                    </div>
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-chevron-right"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </a>
                  <ul
                    className={
                      BuyerManagement.includes(pathname)
                        ? "collapse submenu list-unstyled show"
                        : "collapse submenu list-unstyled"
                    }
                    id="buyerMenu"
                    data-parent="#accordionExample"
                  >
                    <li>
                      <NavLink
                        to="/admin/buyer/list"
                        data-active={
                          buyer.includes(pathname) ? "true" : "false"
                        }
                        aria-expanded={
                          buyer.includes(pathname) ? "true" : "false"
                        }
                        className="dropdown-toggle"
                      >
                        <span
                          className="font-bold text-2xl"
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "white",
                            listStyleType: "none",
                          }}
                        >
                          Buyer Lead
                        </span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/request-for-quotation/list"
                        data-active={
                          RequestForQuotation.includes(pathname)
                            ? "true"
                            : "false"
                        }
                        aria-expanded={
                          RequestForQuotation.includes(pathname)
                            ? "true"
                            : "false"
                        }
                        className="dropdown-toggle"
                      >
                        <span
                          className="font-bold text-2xl"
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "white",
                            listStyleType: "none",
                          }}
                        >
                          Request Quotation
                        </span>
                      </NavLink>
                    </li>
                    {userData?.designation != "Graphic Designer" && (
                      <li>
                        <NavLink
                          to="/admin/supplier-by-lead/list"
                          data-active={
                            SupplierBuyLead.includes(pathname)
                              ? "true"
                              : "false"
                          }
                          aria-expanded={
                            SupplierBuyLead.includes(pathname)
                              ? "true"
                              : "false"
                          }
                          className="dropdown-toggle"
                        >
                          <span
                            className="font-bold text-2xl"
                            style={{
                              fontSize: "12px",
                              fontWeight: "700",
                              color: "white",
                              listStyleType: "none",
                            }}
                          >
                            Supplier Buy Lead
                          </span>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {accessModules.includes("Supplier Management") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <a
                    href="#supplierMenu"
                    data-toggle="collapse"
                    aria-expanded={
                      SupplierManagement.includes(pathname) ? "true" : "false"
                    }
                    className={
                      SupplierManagement.includes(pathname)
                        ? "dropdown-toggle collapsed bg-white"
                        : "dropdown-toggle"
                    }
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-clipboard"
                      >
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                      </svg>
                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        Supplier Management
                      </span>
                    </div>
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-chevron-right"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </a>
                  <ul
                    className={
                      SupplierManagement.includes(pathname)
                        ? "collapse submenu list-unstyled show"
                        : "collapse submenu list-unstyled"
                    }
                    id="supplierMenu"
                    data-parent="#accordionExample"
                  >
                    <li>
                      <NavLink
                        to="/admin/supplier/list"
                        data-active={
                          Supplier.includes(pathname) ? "true" : "false"
                        }
                        aria-expanded={
                          Supplier.includes(pathname) ? "true" : "false"
                        }
                        className="dropdown-toggle"
                      >
                        <span
                          className="font-bold text-2xl"
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "white",
                            listStyleType: "none",
                          }}
                        >
                          Suppliers
                        </span>
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/admin/connection/list"
                        data-active={
                          Connection.includes(pathname) ? "true" : "false"
                        }
                        aria-expanded={
                          Connection.includes(pathname) ? "true" : "false"
                        }
                        className="dropdown-toggle"
                      >
                        <span
                          className="font-bold text-2xl"
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "white",
                            listStyleType: "none",
                          }}
                        >
                          Connection
                        </span>
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/admin/supplier/product/list"
                        data-active={
                          SupplierProduct.includes(pathname) ? "true" : "false"
                        }
                        aria-expanded={
                          SupplierProduct.includes(pathname) ? "true" : "false"
                        }
                        className="dropdown-toggle"
                      >
                        <span
                          className="font-bold text-2xl"
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "white",
                            listStyleType: "none",
                          }}
                        >
                          Product
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}

              {accessModules.includes("GD Management") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <a
                    href="#graphicDesignerMenu"
                    data-toggle="collapse"
                    aria-expanded={
                      pathname.startsWith("/admin/graphic-designer")
                        ? "true"
                        : "false"
                    }
                    className={
                      pathname.startsWith("/admin/graphic-designer")
                        ? "dropdown-toggle collapsed bg-white"
                        : "dropdown-toggle"
                    }
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-image"
                      >
                        <rect
                          x="3"
                          y="3"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        GD Management
                      </span>
                    </div>
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-chevron-right"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </a>
                  <ul
                    className={
                      pathname.startsWith("/admin/graphic-designer")
                        ? "collapse submenu list-unstyled show"
                        : "collapse submenu list-unstyled"
                    }
                    id="graphicDesignerMenu"
                    data-parent="#accordionExample"
                  >
                    <li>
                      <NavLink
                        to="/admin/graphic-designer"
                        end
                        // className={({ isActive }) => (isActive ? "active" : "")}
                        data-active={
                          pathname.startsWith("/admin/graphic-designer") &&
                          !pathname.includes("/connection")
                            ? "true"
                            : "false"
                        }
                        aria-expanded={
                          GraphicalDesignManagement.includes(pathname)
                            ? "true"
                            : "false"
                        }
                      >
                        <span
                          className="font-bold text-2xl"
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "white",
                            listStyleType: "none",
                          }}
                        >
                          List
                        </span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/graphic-designer/connection/list"
                        // className={({ isActive }) => (isActive ? "active" : "")}
                        data-active={
                          pathname.startsWith(
                            "/admin/graphic-designer/connection"
                          )
                            ? "true"
                            : "false"
                        }
                        aria-expanded={
                          GraphicalDesignManagement.includes(pathname)
                            ? "true"
                            : "false"
                        }
                      >
                        <span
                          className="font-bold text-2xl"
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "white",
                            listStyleType: "none",
                          }}
                        >
                          Connection
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}
              {accessModules.includes("Assigned Orders") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <NavLink
                    to={
                      userType === "supplier" && !isActive
                        ? "#"
                        : "/admin/assign-order"
                    }
                    data-active={
                      AssignOrderCode.includes(pathname) ? "true" : "false"
                    }
                    aria-expanded={
                      AssignOrderCode.includes(pathname) ? "true" : "false"
                    }
                    className="dropdown-toggle"
                    onClick={(e) => {
                      if (userType === "supplier" && !isActive) {
                        e.preventDefault();
                        alert(
                          "This menu is not accessible. Please wait for admin approval.",
                        );
                      }
                    }}
                  >
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-assign-order"
                      >
                        <path d="M12 2v20M2 12h20" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>

                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        {AssignOrder}
                        {userType === "supplier" && !isActive && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path
                              d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v10a2 
        2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6-2a2 
        2 0 1 1 4 0v2h-4V6zm1 8a1 1 0 1 1 2 0v2a1 1 0 1 1-2 
        0v-2z"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  </NavLink>
                </li>
              )}
              {/* {accessModules.includes("User Management") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <NavLink
                    to="/admin/UserManagement/list"
                    data-active={
                      UserManagement.includes(pathname) ? "true" : "false"
                    }
                    aria-expanded={
                      UserManagement.includes(pathname) ? "true" : "false"
                    }
                    className="dropdown-toggle"
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 100 100"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="studio-scg-icon"
                      >
                        <circle cx="50" cy="50" r="45" />
                        <text
                          x="50%"
                          y="55%"
                          textAnchor="middle"
                          fontSize="28"
                          fontWeight="bold"
                          fill="currentColor"
                          fontFamily="Arial, sans-serif"
                        >
                          SCG
                        </text>
                      </svg>
                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        User Management
                      </span>
                    </div>
                  </NavLink>
                </li>
              )} */}

              {(isSubAdminWithId1 ||
                accessModules.includes("Design Studio")) && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <NavLink
                    to="/admin/design-studio"
                    data-active={
                      DesignStudio.includes(pathname) ? "true" : "false"
                    }
                    aria-expanded={
                      DesignStudio.includes(pathname) ? "true" : "false"
                    }
                    className="dropdown-toggle"
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 100 100"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="studio-scg-icon"
                      >
                        <circle cx="50" cy="50" r="45" />
                        <text
                          x="50%"
                          y="55%"
                          textAnchor="middle"
                          fontSize="28"
                          fontWeight="bold"
                          fill="currentColor"
                          fontFamily="Arial, sans-serif"
                        >
                          SCG
                        </text>
                      </svg>

                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        Design Studio
                      </span>
                    </div>
                  </NavLink>
                </li>
              )}

              {accessModules.includes("Assign Order") && (
                <li
                  className="menu"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <NavLink
                    to="/admin/assign-order"
                    data-active={
                      AssignOrderCode.includes(pathname) ? "true" : "false"
                    }
                    aria-expanded={
                      AssignOrderCode.includes(pathname) ? "true" : "false"
                    }
                    className="dropdown-toggle"
                  >
                    <div className="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-assign-order"
                      >
                        <path d="M12 2v20M2 12h20" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>

                      <span
                        className="font-bold text-2xl"
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        Assign Order
                      </span>
                    </div>
                  </NavLink>
                </li>
              )}

              <div className="block md:hidden px-4 py-2 text-sm font-semibold text-gray-700"></div>
              <div className="h-[1px] bg-gray-300 my-2 md:hidden"></div>
              <li
                className="menu mobile-only"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <div className="dropdown-toggle" onClick={() => LogoutAdmin()}>
                  <div className="">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-log-out"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    <span
                      className="font-bold text-2xl"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      Logout
                    </span>
                  </div>
                </div>
              </li>
            </ul>
            <SidebarUserCard user={userData} />
          </nav>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
  @media screen and (min-width: 768px) {
    .mobile-only {
      display: none;
    }
  }
`}</style>

      <div className="sidebar-wrapper sidebar-theme">
        <nav id="sidebar">
          <SidebarBrand />
          <SidebarSearch />
          <div className="shadow-bottom"></div>
          <div className="d-flex justify-content-end">
            <a
              href="/#"
              className="sidebarCollapse mr-2 d-sm-none"
              data-placement="bottom"
            >
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-x"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </a>
          </div>

          <ul
            className="list-unstyled menu-categories scroll-ui"
            id="accordionExample"
            style={{ overflowY: "scroll" }}
          >
            <li
              className="menu mobile-only"
              style={{ borderBottom: "1px solid #e0e0e0", marginTop: "15px" }}
            >
              <NavLink
                to="/admin/profile-update"
                data-active={
                  pathname === "/admin/profile-update" ? "true" : "false"
                }
                aria-expanded={
                  pathname === "/admin/profile-update" ? "true" : "false"
                }
                className="dropdown-toggle"
              >
                <div className="flex items-center">
                  <img
                    src="/assets/img/MyPackGenie Final.jpg"
                    alt="Profile Icon"
                    width="24"
                    height="24"
                    className="mr-2"
                    style={{ objectFit: "contain" }}
                  />
                  <span
                    className="font-bold"
                    style={{ fontSize: "12px", fontWeight: "700" }}
                  >
                    Profile
                  </span>
                </div>
              </NavLink>
            </li>

            <Caption>Workspace</Caption>

            <li
              className="menu"
              style={{ borderBottom: "1px solid #e0e0e0", marginTop: "15px" }}
            >
              <NavLink
                to="/admin/dashboard"
                data-active={pathname === "/admin/dashboard" ? "true" : "false"}
                aria-expanded={
                  pathname === "/admin/dashboard" ? "true" : "false"
                }
                className="dropdown-toggle"
              >
                <div className="">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-home"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  <span
                    className="font-bold text-2xl"
                    style={{ fontSize: "12px", fontWeight: "700" }}
                  >
                    Dashboard
                  </span>
                </div>
              </NavLink>
            </li>

            {userType === "admin" && (
              <li
                className="menu"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <NavLink
                  to="/admin/new-registration"
                  data-active={
                    pathname === "/admin/new-registration" ? "true" : "false"
                  }
                  aria-expanded={
                    pathname === "/admin/new-registration" ? "true" : "false"
                  }
                  className="dropdown-toggle"
                >
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.8}
                      stroke="currentColor"
                      className="text-blue-600"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18 9v6M21 12h-6M7.5 7.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM4 19.5a5.5 5.5 0 0 1 11 0v.25a.25.25 0 0 1-.25.25H4.25a.25.25 0 0 1-.25-.25v-.25Z"
                      />
                    </svg>

                    <span className="text-sm font-semibold text-gray-800">
                      New Registration
                    </span>
                  </div>
                </NavLink>
              </li>
            )}

            {userType === "admin" && (
              <li
                className="menu"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <NavLink
                  to="/admin/UserManagement/list"
                  data-active={
                    UserManagement.includes(pathname) ? "true" : "false"
                  }
                  aria-expanded={
                    UserManagement.includes(pathname) ? "true" : "false"
                  }
                  className="dropdown-toggle"
                >
                  <div className="">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 100 100"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="studio-scg-icon"
                    >
                      <circle cx="50" cy="50" r="45" />
                      <text
                        x="50%"
                        y="55%"
                        textAnchor="middle"
                        fontSize="28"
                        fontWeight="bold"
                        fill="currentColor"
                        fontFamily="Arial, sans-serif"
                      >
                        SCG
                      </text>
                    </svg>
                    <span
                      className="font-bold text-2xl"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      User Management
                    </span>
                  </div>
                </NavLink>
              </li>
            )}

            <Caption>Marketplace</Caption>

            {userType === "admin" && (
              <li
                className="menu"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <a
                  href="#buyerMenu"
                  data-toggle="collapse"
                  aria-expanded={
                    BuyerManagement.includes(pathname) ? "true" : "false"
                  }
                  className={
                    BuyerManagement.includes(pathname)
                      ? "dropdown-toggle collapsed bg-white"
                      : "dropdown-toggle"
                  }
                >
                  <div className="">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-layers"
                    >
                      <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                      <polyline points="2 17 12 22 22 17"></polyline>
                      <polyline points="2 12 12 17 22 12"></polyline>
                    </svg>
                    <span
                      className="font-bold text-2xl"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      Buyer Management
                    </span>
                  </div>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-chevron-right"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </a>
                <ul
                  className={
                    BuyerManagement.includes(pathname)
                      ? "collapse submenu list-unstyled show"
                      : "collapse submenu list-unstyled"
                  }
                  id="buyerMenu"
                  data-parent="#accordionExample"
                >
                  <li>
                    <NavLink
                      to="/admin/buyer/list"
                      data-active={buyer.includes(pathname) ? "true" : "false"}
                      aria-expanded={
                        buyer.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Buyer Lead
                      </span>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/admin/request-for-quotation/list"
                      data-active={
                        RequestForQuotation.includes(pathname)
                          ? "true"
                          : "false"
                      }
                      aria-expanded={
                        RequestForQuotation.includes(pathname)
                          ? "true"
                          : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Request Quotation
                      </span>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/admin/supplier-by-lead/list"
                      data-active={
                        SupplierBuyLead.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        SupplierBuyLead.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Supplier Buy Lead
                      </span>
                    </NavLink>
                  </li>
                </ul>
              </li>
            )}

            {userType === "admin" && (
              <li
                className="menu"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <a
                  href="#supplierMenu"
                  data-toggle="collapse"
                  aria-expanded={
                    SupplierManagement.includes(pathname) ? "true" : "false"
                  }
                  className={
                    SupplierManagement.includes(pathname)
                      ? "dropdown-toggle collapsed bg-white"
                      : "dropdown-toggle"
                  }
                >
                  <div className="">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-clipboard"
                    >
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                    </svg>
                    <span
                      className="font-bold text-2xl"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      Supplier Management
                    </span>
                  </div>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-chevron-right"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </a>
                <ul
                  className={
                    SupplierManagement.includes(pathname)
                      ? "collapse submenu list-unstyled show"
                      : "collapse submenu list-unstyled"
                  }
                  id="supplierMenu"
                  data-parent="#accordionExample"
                >
                  <li>
                    <NavLink
                      to="/admin/supplier/list"
                      data-active={
                        Supplier.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        Supplier.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Suppliers
                      </span>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/admin/connection/list"
                      data-active={
                        Connection.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        Connection.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Connection
                      </span>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/admin/supplier/product/list"
                      data-active={
                        SupplierProduct.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        SupplierProduct.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Product
                      </span>
                    </NavLink>
                  </li>
                </ul>
              </li>
            )}
            {userType === "admin" && (
              <li
                className="menu"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <a
                  href="#graphicDesignerMenu"
                  data-toggle="collapse"
                  aria-expanded={
                    pathname.startsWith("/admin/graphic-designer")
                      ? "true"
                      : "false"
                  }
                  className={
                    pathname.startsWith("/admin/graphic-designer")
                      ? "dropdown-toggle collapsed bg-white"
                      : "dropdown-toggle"
                  }
                >
                  <div className="">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-image"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span
                      className="font-bold text-2xl"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      GD Management
                    </span>
                  </div>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-chevron-right"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </a>
                <ul
                  className={
                    pathname.startsWith("/admin/graphic-designer")
                      ? "collapse submenu list-unstyled show"
                      : "collapse submenu list-unstyled"
                  }
                  id="graphicDesignerMenu"
                  data-parent="#accordionExample"
                >
                  <li>
                    <NavLink
                      to="/admin/graphic-designer"
                      end
                      // className={({ isActive }) => (isActive ? "active" : "")}
                      data-active={
                        pathname.startsWith("/admin/graphic-designer") &&
                        !pathname.includes("/connection")
                          ? "true"
                          : "false"
                      }
                      aria-expanded={
                        GraphicalDesignManagement.includes(pathname)
                          ? "true"
                          : "false"
                      }
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        List
                      </span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/admin/graphic-designer/connection/list"
                      // className={({ isActive }) => (isActive ? "active" : "")}
                      data-active={
                        pathname.startsWith(
                          "/admin/graphic-designer/connection"
                        )
                          ? "true"
                          : "false"
                      }
                      aria-expanded={
                        GraphicalDesignManagement.includes(pathname)
                          ? "true"
                          : "false"
                      }
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Connection
                      </span>
                    </NavLink>
                  </li>
                </ul>
              </li>
            )}

            {userType === "supplier" && (
              <li
                className="menu"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <a
                  href="#supplierMenu"
                  data-toggle="collapse"
                  aria-expanded={
                    SupplierManagement.includes(pathname) ? "true" : "false"
                  }
                  className={
                    SupplierManagement.includes(pathname)
                      ? "dropdown-toggle collapsed bg-white"
                      : "dropdown-toggle"
                  }
                >
                  <div className="">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-clipboard"
                    >
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                    </svg>
                    <span
                      className="font-bold text-2xl"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      Supplier Management
                    </span>
                  </div>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-chevron-right"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </a>
                <ul
                  className={
                    SupplierManagement.includes(pathname)
                      ? "collapse submenu list-unstyled show"
                      : "collapse submenu list-unstyled"
                  }
                  id="supplierMenu"
                  data-parent="#accordionExample"
                >
                  <li>
                    <NavLink
                      to={
                        userType === "supplier" && !isActive
                          ? "#"
                          : "/admin/inbound"
                      }
                      data-active={
                        Supplier.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        Supplier.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                      onClick={(e) => {
                        if (userType === "supplier" && !isActive) {
                          e.preventDefault();
                          alert(
                            "This menu is not accessible. Please wait for admin approval.",
                          );
                        }
                      }}
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Inbound
                        {userType === "supplier" && !isActive && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path
                              d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v10a2 
          2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6-2a2 
          2 0 1 1 4 0v2h-4V6zm1 8a1 1 0 1 1 2 0v2a1 1 0 1 1-2 
          0v-2z"
                            />
                          </svg>
                        )}
                      </span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={
                        userType === "supplier" && !isActive
                          ? "#"
                          : "/admin/outbound"
                      }
                      data-active={
                        Supplier.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        Supplier.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                      onClick={(e) => {
                        if (userType === "supplier" && !isActive) {
                          e.preventDefault();
                          alert(
                            "This menu is not accessible. Please wait for admin approval.",
                          );
                        }
                      }}
                    >
                      <span
                        className="flex items-center gap-1 font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        OutBound
                        {userType === "supplier" && !isActive && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path
                              d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v10a2 
          2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6-2a2 
          2 0 1 1 4 0v2h-4V6zm1 8a1 1 0 1 1 2 0v2a1 1 0 1 1-2 
          0v-2z"
                            />
                          </svg>
                        )}
                      </span>
                    </NavLink>
                  </li>
                </ul>
              </li>
            )}
            {userType === "subadmin" && sub_admin_id == 1 && (
              <li
                className="menu"
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <NavLink
                  to={
                    userType === "supplier" && !isActive
                      ? "#"
                      : "/admin/design-studio"
                  }
                  data-active={
                    DesignStudio.includes(pathname) ? "true" : "false"
                  }
                  aria-expanded={
                    DesignStudio.includes(pathname) ? "true" : "false"
                  }
                  className="dropdown-toggle"
                  onClick={(e) => {
                    if (userType === "supplier" && !isActive) {
                      e.preventDefault();
                      alert(
                        "This menu is not accessible. Please wait for admin approval.",
                      );
                    }
                  }}
                >
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 100 100"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="studio-scg-icon"
                    >
                      <circle cx="50" cy="50" r="45" />
                      <text
                        x="50%"
                        y="55%"
                        textAnchor="middle"
                        fontSize="28"
                        fontWeight="bold"
                        fill="currentColor"
                        fontFamily="Arial, sans-serif"
                      >
                        SCG
                      </text>
                    </svg>

                    <span
                      className="font-bold text-2xl"
                      style={{ fontSize: "12px", fontWeight: "700" }}
                    >
                      Design Studio
                      {userType === "supplier" && !isActive && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v10a2 
        2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6-2a2 
        2 0 1 1 4 0v2h-4V6zm1 8a1 1 0 1 1 2 0v2a1 1 0 1 1-2 
        0v-2z"
                          />
                        </svg>
                      )}
                    </span>
                  </div>
                </NavLink>
              </li>
            )}

            <li className="menu" style={{ borderBottom: "1px solid #e0e0e0" }}>
              <NavLink
                to={
                  userType === "supplier" && !isActive
                    ? "#"
                    : "/admin/assign-order"
                }
                data-active={
                  AssignOrderCode.includes(pathname) ? "true" : "false"
                }
                aria-expanded={
                  AssignOrderCode.includes(pathname) ? "true" : "false"
                }
                className="dropdown-toggle"
                onClick={(e) => {
                  if (userType === "supplier" && !isActive) {
                    e.preventDefault();
                    alert(
                      "This menu is not accessible. Please wait for admin approval.",
                    );
                  }
                }}
              >
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-assign-order"
                  >
                    <path d="M12 2v20M2 12h20" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>

                  <span
                    className="font-bold text-2xl"
                    style={{ fontSize: "12px", fontWeight: "700" }}
                  >
                    {AssignOrder}
                    {userType === "supplier" && !isActive && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v10a2 
        2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6-2a2 
        2 0 1 1 4 0v2h-4V6zm1 8a1 1 0 1 1 2 0v2a1 1 0 1 1-2 
        0v-2z"
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </NavLink>
            </li>

            <Caption>Catalog</Caption>

            <li className="menu" style={{ borderBottom: "1px solid #e0e0e0" }}>
              <a
                href="#datatables"
                data-toggle="collapse"
                aria-expanded={Master.includes(pathname) ? "true" : "false"}
                className={
                  Master.includes(pathname)
                    ? "dropdown-toggle collapsed bg-white"
                    : "dropdown-toggle"
                }
              >
                <div className="">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-layers"
                  >
                    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                    <polyline points="2 17 12 22 22 17"></polyline>
                    <polyline points="2 12 12 17 22 12"></polyline>
                  </svg>
                  <span
                    className="font-bold text-2xl"
                    style={{ fontSize: "12px", fontWeight: "700" }}
                  >
                    Master
                  </span>
                </div>
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-right"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </a>
              <ul
                className={
                  Master.includes(pathname)
                    ? "collapse submenu list-unstyled show"
                    : "collapse submenu list-unstyled"
                }
                id="datatables"
                data-parent="#accordionExample"
              >
                <li>
                  <NavLink
                    to={
                      userType === "supplier" && !isActive
                        ? "#"
                        : "/admin/product"
                    }
                    data-active={Product.includes(pathname) ? "true" : "false"}
                    aria-expanded={
                      Product.includes(pathname) ? "true" : "false"
                    }
                    className="dropdown-toggle"
                    onClick={(e) => {
                      if (userType === "supplier" && !isActive) {
                        e.preventDefault(); // stop navigation
                        alert(
                          "This menu is not accessible. Please wait for admin approval.",
                        );
                      }
                    }}
                  >
                    <span
                      className="font-bold text-2xl"
                      style={{
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "white",
                        listStyleType: "none",
                      }}
                    >
                      {product}
                      {userType === "supplier" && !isActive && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v10a2 
        2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6-2a2 
        2 0 1 1 4 0v2h-4V6zm1 8a1 1 0 1 1 2 0v2a1 1 0 1 1-2 
        0v-2z"
                          />
                        </svg>
                      )}
                    </span>
                  </NavLink>
                </li>

                {userType === "admin" && (
                  <li>
                    <NavLink
                      to="/admin/category"
                      data-active={
                        Category.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        Category.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Category
                      </span>
                    </NavLink>
                  </li>
                )}

                {userType === "admin" && (
                  <li>
                    <NavLink
                      to="/admin/sub-category"
                      data-active={
                        SubCategory.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        SubCategory.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Sub Category
                      </span>
                    </NavLink>
                  </li>
                )}
                {userType === "admin" && (
                  <li>
                    <NavLink
                      to="/admin/blog"
                      data-active={Blog.includes(pathname) ? "true" : "false"}
                      aria-expanded={Blog.includes(pathname) ? "true" : "false"}
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Blog
                      </span>
                    </NavLink>
                  </li>
                )}
                {userType === "admin" && (
                  <li>
                    <NavLink
                      to="/admin/blog-category"
                      data-active={
                        BlogCategory.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        BlogCategory.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Blog Category
                      </span>
                    </NavLink>
                  </li>
                )}

                {userType === "admin" && (
                  <li>
                    <NavLink
                      to="/admin/contacts"
                      data-active={
                        Contacts.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        Contacts.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        RFQs
                      </span>
                    </NavLink>
                  </li>
                )}

                {userType === "admin" && (
                  <li>
                    <NavLink
                      to="/admin/consultations"
                      data-active={
                        Consultations.includes(pathname) ? "true" : "false"
                      }
                      aria-expanded={
                        Consultations.includes(pathname) ? "true" : "false"
                      }
                      className="dropdown-toggle"
                    >
                      <span
                        className="font-bold text-2xl"
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "white",
                          listStyleType: "none",
                        }}
                      >
                        Consultations
                      </span>
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
            <div className="block md:hidden px-4 py-2 text-sm font-semibold text-gray-700"></div>
            <div className="h-[1px] bg-gray-300 my-2 md:hidden"></div>

            <li
              className="menu mobile-only"
              style={{ borderBottom: "1px solid #e0e0e0" }}
            >
              <div className="dropdown-toggle" onClick={() => LogoutAdmin()}>
                <div className="">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-log-out"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span
                    className="font-bold text-2xl"
                    style={{ fontSize: "12px", fontWeight: "700" }}
                  >
                    Logout
                  </span>
                </div>
              </div>
            </li>
          </ul>
          <SidebarUserCard user={userData} />
        </nav>
      </div>
    </>
  );
};

export default Admin_Sidebar;
